'use client';

import { useEffect, useState } from 'react';
import { apiGet } from '@/lib/apiClient';
import { getAuthToken } from '@/utils/auth';

/**
 * The signed-in SuperAdmin's own effective permissions.
 *   { isOwner, inactive, sections: { <key>: { view, manage } }, catalogue }
 * Owners bypass everything. Sub-users get only what an owner granted.
 * Module-cached (60s) + de-duped like usePermissions.
 */
let _cache = null;
let _inflight = null;
const FRESH_FOR_MS = 60 * 1000;

/** Empty (all-false) permission set — what a non-superadmin gets. */
const EMPTY_SA = { isOwner: false, inactive: false, sections: {}, catalogue: [] };

const fetchSAPermissions = async () => {
  // The Sidebar mounts this hook for EVERY role, so a schooladmin/teacher
  // session used to fire /api/super-admin/permissions/me and log a 403 in
  // the console on every page load. Only a superadmin can have SuperAdmin
  // permissions, so short-circuit instead of asking the server.
  if (typeof window !== 'undefined') {
    try {
      if (sessionStorage.getItem('userRole') !== 'superadmin') return EMPTY_SA;
    } catch {
      /* storage blocked — fall through and ask the server */
    }
  }
  // The cache belongs to ONE signed-in user. Signing out and back in as
  // someone else (a sub-user, then the owner) used to keep serving the
  // first account's sections for up to a minute — the sidebar only came
  // right after a hard refresh.
  const token = getAuthToken() || '';
  if (_cache && _cache.token === token && Date.now() - _cache.fetchedAt < FRESH_FOR_MS) return _cache.data;
  if (_inflight && _inflight.token === token) return _inflight.promise;
  const promise = (async () => {
    try {
      const res = await apiGet('/api/super-admin/permissions/me');
      const data = {
        isOwner: !!res?.isOwner,
        inactive: !!res?.inactive,
        sections: res?.sections || {},
        catalogue: res?.catalogue || [],
      };
      _cache = { fetchedAt: Date.now(), token, data };
      return data;
    } finally {
      if (_inflight?.token === token) _inflight = null;
    }
  })();
  _inflight = { token, promise };
  return promise;
};

export const invalidateSAPermissions = () => {
  _cache = null;
  _inflight = null;
};

const decide = (data, section, op = 'view') => {
  if (!data) return false;
  if (data.isOwner) return true;
  const s = data.sections?.[section];
  if (!s) return false;
  return op === 'manage' ? !!s.manage : !!s.view || !!s.manage;
};

/** Synchronous read for gate-and-go callers (uses the module cache, for the current user only). */
export const saCanSync = (section, op = 'view') =>
  decide(_cache && _cache.token === (getAuthToken() || '') ? _cache.data : null, section, op);

export function useSuperAdminPermissions() {
  const [state, setState] = useState(() => ({
    isOwner: false,
    inactive: false,
    sections: {},
    catalogue: [],
    loading: true,
    error: null,
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSAPermissions();
        if (!cancelled) setState({ ...data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          setState((p) => ({ ...p, loading: false, error: err?.message || 'Permission lookup failed' }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const can = (section, op = 'view') => decide(state, section, op);
  return { ...state, can, canView: (s) => can(s, 'view'), canManage: (s) => can(s, 'manage') };
}
